import Model from '@/models/Model.js';

/**
 * InstitutionSetting Model
 *
 * Manages institution-wide configuration settings including:
 * - Institution name and branding
 * - Default timezone for the application
 * - Institution logo
 * - Locale preferences
 *
 * Settings are stored in the database and cached for performance.
 * This model provides both VuexORM integration and a fallback cache
 * for resilience when VuexORM is unavailable.
 */
export default class InstitutionSetting extends Model {
    static get entity() {
        return 'institution_settings';
    }

    static get modelName() {
        return 'InstitutionSetting';
    }

    static fields() {
        return {
            id: this.attr(null),
            key: this.attr(null),
            value: this.attr(null),
            type: this.attr('string'),
            description: this.attr(null),

            ...super.fields(),
        };
    }

    /**
     * Get all settings as a key-value object.
     *
     * Uses VuexORM store when available, falls back to cached data if store
     * is empty or inaccessible. Includes frontend defaults for required fields.
     *
     * @returns {Object<string, any>} Settings object with keys and values
     * @example
     * const settings = InstitutionSetting.getSettings();
     * console.log(settings.institution_name); // "My Institution"
     */
    static getSettings() {
        let settings = {};

        try {
            const allSettings = InstitutionSetting.all();

            allSettings.forEach(setting => {
                settings[setting.key] = setting.value;
            });

            // If VuexORM store is empty or problematic, use fallback cache
            if (allSettings.length === 0 || (allSettings.length === 1 && allSettings[0].key === null)) {
                if (InstitutionSetting._fallbackCache) {
                    settings = { ...InstitutionSetting._fallbackCache };
                }
            }
        } catch (error) {
            console.warn('⚠️ VuexORM access failed, using fallback cache:', error);
            if (InstitutionSetting._fallbackCache) {
                settings = { ...InstitutionSetting._fallbackCache };
            }
        }

        // Add frontend-only defaults for form fields when they're missing from database
        // Note: These are ONLY used if the database has no value (config file defaults are excluded on backend)
        const frontendDefaults = {
            institution_name: settings.institution_name || '',
            institution_logo: settings.institution_logo || '/img/app__logo.png',
            institution_timezone: settings.institution_timezone || 'America/Los_Angeles',
            institution_locale: settings.institution_locale || 'en-US',
            certificate_layout: settings.certificate_layout || 'classic',
            certificate_signed_by: settings.certificate_signed_by || '',
            certificate_signed_by_title: settings.certificate_signed_by_title || '',
            institution_support_email: settings.institution_support_email || '',
            edverum_support_email: settings.edverum_support_email || '',
            access_denied_helper_text:
                settings.access_denied_helper_text ||
                'Please contact the Edverum Support Team if you believe you have been denied access in error.',
            enrollment_email_time: settings.enrollment_email_time || '8:00 AM',
            missing_submissions_email_time: settings.missing_submissions_email_time || '5:00 AM',
            failed_course_email_time: settings.failed_course_email_time || '5:00 AM',
            identity_verification_enabled: settings.identity_verification_enabled ?? false,
            identity_verification_min_minutes: settings.identity_verification_min_minutes || 10080,
            identity_verification_max_minutes: settings.identity_verification_max_minutes || 20160,
            identity_verification_minutes_locked_out: settings.identity_verification_minutes_locked_out || 5,
            risk_activity_due_date_buffer_delay: settings.risk_activity_due_date_buffer_delay || 7,
        };

        return { ...settings, ...frontendDefaults };
    }

    /**
     * Find a setting by its key.
     *
     * @param {string} key - The setting key to search for
     * @returns {InstitutionSetting} The setting instance (creates a new one if not found)
     * @example
     * const nameSetting = InstitutionSetting.findByKey('institution_name');
     */
    static findByKey(key) {
        const setting = InstitutionSetting.query().where('key', key).first();
        return setting || new InstitutionSetting({ key });
    }

    /**
     * Get a setting value by key with optional fallback.
     *
     * @param {string} key - The setting key to retrieve
     * @param {any} [defaultValue=null] - Fallback value if setting not found
     * @returns {any} The setting value or default value
     * @example
     * const timezone = InstitutionSetting.getValue('institution_timezone', 'America/Los_Angeles');
     */
    static getValue(key, defaultValue = null) {
        const setting = this.findByKey(key);
        return setting.value || defaultValue;
    }

    /**
     * Fetch all institution settings from the API.
     *
     * Loads settings from backend and stores them in VuexORM store.
     * If VuexORM storage fails, uses a fallback cache mechanism.
     *
     * @returns {Promise<Object<string, any>>} Settings object with keys and values
     * @throws {Error} If API request fails
     * @example
     * try {
     *     await InstitutionSetting.fetchSettings();
     *     console.log('Settings loaded successfully');
     * } catch (error) {
     *     console.error('Failed to load settings:', error);
     * }
     */
    static async fetchSettings() {
        const response = await InstitutionSetting.api().get('/api/institution-settings');

        // VuexORM wraps the response, and Laravel GenericResource wraps it again
        // So the path is: response.response.data.data (Laravel's GenericResource wrapper)
        const settingsData = response.response?.data?.data || response.data?.data || response.data || response;

        const settingsArray = Object.keys(settingsData).map(key => ({
            id: key, // Use key as ID for consistency
            key,
            value: settingsData[key],
            type: typeof settingsData[key],
        }));

        try {
            InstitutionSetting.insertOrUpdate({ data: settingsArray });
        } catch (_vuexOrmError) {
            InstitutionSetting._fallbackCache = settingsData;
        }

        return settingsData;
    }

    /**
     * Fetch the certificate-related settings from the API.
     *
     * Uses the certificate-scoped endpoint (Owner/Admin/Instruction Admin) rather
     * than the Owner/Admin-only settings index, so Instruction Admins can load the
     * Certificate Settings page. Stores the returned keys
     * (certificate_layout, certificate_signed_by, certificate_signed_by_title,
     * institution_name) in VuexORM.
     *
     * @returns {Promise<Object<string, any>>} Certificate settings keyed by name
     * @throws {Error} If API request fails
     */
    static async fetchCertificateSettings() {
        const response = await InstitutionSetting.api().get('/api/institution-settings/certificates');

        const settingsData = response.response?.data?.data || response.data?.data || response.data || response;

        const settingsArray = Object.keys(settingsData).map(key => ({
            id: key,
            key,
            value: settingsData[key],
            type: typeof settingsData[key],
        }));

        try {
            InstitutionSetting.insertOrUpdate({ data: settingsArray });
        } catch (_vuexOrmError) {
            InstitutionSetting._fallbackCache = { ...InstitutionSetting._fallbackCache, ...settingsData };
        }

        return settingsData;
    }

    /**
     * Update institution profile settings via API.
     *
     * Accepts FormData for file uploads (institution logo). Updates the store
     * with returned settings data or falls back to cache on VuexORM failure.
     *
     * @param {FormData} formData - Form data containing institution settings
     *   - institution_name {string} - Institution name
     *   - institution_timezone {string} - IANA timezone identifier
     *   - institution_locale {string} - Locale code (e.g., 'en-US')
     *   - institution_logo {File} [optional] - Logo image file
     * @returns {Promise<Object>} Response data from API
     * @throws {Error} If API request fails or validation errors occur
     * @example
     * const formData = new FormData();
     * formData.append('institution_name', 'My University');
     * formData.append('institution_timezone', 'America/New_York');
     * formData.append('institution_locale', 'en-US');
     * formData.append('institution_logo', logoFile);
     *
     * try {
     *     await InstitutionSetting.updateProfile(formData);
     * } catch (error) {
     *     console.error('Update failed:', error.response?.data?.errors);
     * }
     */
    static async updateProfile(formData) {
        const response = await InstitutionSetting.api().post('/api/institution-settings/profile', formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });

        // VuexORM wraps the response, and Laravel GenericResource wraps it again
        // So the path is: response.response.data.data (Laravel's GenericResource wrapper)
        const responseData = response.response?.data?.data || response.data?.data || response.data || response;

        const settingsData = responseData?.settings || responseData;

        // Update the store with the returned settings
        if (settingsData && typeof settingsData === 'object' && !Array.isArray(settingsData)) {
            try {
                const settingsArray = Object.keys(settingsData).map(key => ({
                    id: key,
                    key,
                    value: settingsData[key],
                    type: typeof settingsData[key],
                }));

                InstitutionSetting.insertOrUpdate({ data: settingsArray });
            } catch (_vuexOrmError) {
                InstitutionSetting._fallbackCache = { ...InstitutionSetting._fallbackCache, ...settingsData };
            }
        }

        return responseData;
    }

    /**
     * Fetch available timezones from the API.
     *
     * @returns {Promise<Array<{value: string, label: string}>>} Array of timezone options
     * @throws {Error} If API request fails
     * @example
     * try {
     *     const timezones = await InstitutionSetting.fetchTimezones();
     *     console.log(timezones); // [{ value: 'America/New_York', label: '...' }, ...]
     * } catch (error) {
     *     console.error('Failed to load timezones:', error);
     * }
     */
    static async fetchTimezones() {
        const response = await InstitutionSetting.api().get('/api/institution-settings/timezones');

        return response.response?.data?.data || response.data?.data || response.data || response;
    }

    /**
     * Update contact & support settings via API.
     *
     * Updates institution support email, Edverum support email, and access denied helper text.
     *
     * @param {Object} settingsData - Object containing contact & support settings
     *   - institution_support_email {string} - Institution support email address
     *   - edverum_support_email {string} - Edverum support email address
     *   - access_denied_helper_text {string} - Guidance shown on access denied experiences
     * @returns {Promise<Object>} Response data from API
     * @throws {Error} If API request fails or validation errors occur
     * @example
     * const settingsData = {
     *     institution_support_email: 'support@institution.edu',
     *     edverum_support_email: 'support@edverum.com',
     *     access_denied_helper_text: 'Please contact the Edverum Support Team if you believe you have been denied access in error.',
     * };
     *
     * try {
     *     await InstitutionSetting.updateContactSupport(settingsData);
     * } catch (error) {
     *     console.error('Update failed:', error.response?.data?.errors);
     * }
     */
    static async updateContactSupport(settingsData) {
        const response = await InstitutionSetting.api().post('/api/institution-settings/contact-support', settingsData);

        // VuexORM wraps the response, and Laravel GenericResource wraps it again
        // So the path is: response.response.data.data (Laravel's GenericResource wrapper)
        const responseData = response.response?.data?.data || response.data?.data || response.data || response;

        const updatedSettings = responseData?.settings || responseData;

        // Update the store with the returned settings
        if (updatedSettings && typeof updatedSettings === 'object' && !Array.isArray(updatedSettings)) {
            try {
                const settingsArray = Object.keys(updatedSettings).map(key => ({
                    id: key,
                    key,
                    value: updatedSettings[key],
                    type: typeof updatedSettings[key],
                }));

                InstitutionSetting.insertOrUpdate({ data: settingsArray });
            } catch (_vuexOrmError) {
                InstitutionSetting._fallbackCache = { ...InstitutionSetting._fallbackCache, ...updatedSettings };
            }
        }

        return responseData;
    }

    /**
     * Get institution support email with fallback logic.
     *
     * Returns institution_support_email if available, otherwise falls back to
     * support_email, and finally to a default value.
     *
     * @param {string} [defaultValue='support@edverum.com'] - Default email if no setting is found
     * @returns {string} Support email address
     * @example
     * const email = InstitutionSetting.getSupportEmail();
     * // Returns: 'support@institution.edu' or fallback value
     */
    static getSupportEmail(defaultValue = 'support@edverum.com') {
        const settings = InstitutionSetting.getSettings();
        return settings?.institution_support_email || settings?.support_email || defaultValue;
    }

    /**
     * Update system notifications settings via API.
     *
     * @param {Object} settingsData - Object containing system notifications settings
     *   - failure_emails {string[]} - Email recipients for system failure notifications
     * @returns {Promise<Object>} Response data from API
     */
    static async updateSystemNotifications(settingsData) {
        const response = await InstitutionSetting.api().post(
            '/api/institution-settings/system-notifications',
            settingsData
        );

        const responseData = response.response?.data?.data || response.data?.data || response.data || response;
        const updatedSettings = responseData?.settings || responseData;

        if (updatedSettings && typeof updatedSettings === 'object' && !Array.isArray(updatedSettings)) {
            try {
                const settingsArray = Object.keys(updatedSettings).map(key => ({
                    id: key,
                    key,
                    value: updatedSettings[key],
                    type: typeof updatedSettings[key],
                }));

                InstitutionSetting.insertOrUpdate({ data: settingsArray });
            } catch (_vuexOrmError) {
                InstitutionSetting._fallbackCache = { ...InstitutionSetting._fallbackCache, ...updatedSettings };
            }
        }

        return responseData;
    }

    /**
     * Update certificate settings via API.
     *
     * @param {Object} settingsData - Object containing certificate settings
     *   - certificate_layout {string} - Selected certificate layout
     *   - certificate_signed_by {string} - Signer name shown on certificates
     *   - certificate_signed_by_title {string} - Optional signer title or role
     * @returns {Promise<Object>} Response data from API
     */
    static async updateCertificates(settingsData) {
        const response = await InstitutionSetting.api().post('/api/institution-settings/certificates', settingsData);

        const responseData = response.response?.data?.data || response.data?.data || response.data || response;
        const updatedSettings = responseData?.settings || responseData;

        if (updatedSettings && typeof updatedSettings === 'object' && !Array.isArray(updatedSettings)) {
            try {
                const settingsArray = Object.keys(updatedSettings).map(key => ({
                    id: key,
                    key,
                    value: updatedSettings[key],
                    type: typeof updatedSettings[key],
                }));

                InstitutionSetting.insertOrUpdate({ data: settingsArray });
            } catch (_vuexOrmError) {
                InstitutionSetting._fallbackCache = { ...InstitutionSetting._fallbackCache, ...updatedSettings };
            }
        }

        return responseData;
    }

    /**
     * Update email & reminders settings via API.
     *
     * Updates enrollment email time, missing submissions email time, and failed course email time settings.
     *
     * @param {Object} settingsData - Object containing email & reminders settings
     *   - enrollment_email_time {string} - Daily time for enrollment confirmation emails
     *   - missing_submissions_email_time {string} - Daily time for course failures emails
     *   - failed_course_email_time {string} - Daily time for missing submissions emails
     * @returns {Promise<Object>} Response data from API
     * @throws {Error} If API request fails or validation errors occur
     * @example
     * const settingsData = {
     *     enrollment_email_time: '8:00 AM',
     *     missing_submissions_email_time: '5:00 AM',
     *     failed_course_email_time: '5:00 AM',
     * };
     *
     * try {
     *     await InstitutionSetting.updateEmailReminders(settingsData);
     * } catch (error) {
     *     console.error('Update failed:', error.response?.data?.errors);
     * }
     */
    static async updateEmailReminders(settingsData) {
        const response = await InstitutionSetting.api().post('/api/institution-settings/email-reminders', settingsData);

        const responseData = response.response?.data?.data || response.data?.data || response.data || response;

        const updatedSettings = responseData?.settings || responseData;

        if (updatedSettings && typeof updatedSettings === 'object' && !Array.isArray(updatedSettings)) {
            try {
                const settingsArray = Object.keys(updatedSettings).map(key => ({
                    id: key,
                    key,
                    value: updatedSettings[key],
                    type: typeof updatedSettings[key],
                }));

                InstitutionSetting.insertOrUpdate({ data: settingsArray });
            } catch (_vuexOrmError) {
                InstitutionSetting._fallbackCache = { ...InstitutionSetting._fallbackCache, ...updatedSettings };
            }
        }

        return responseData;
    }

    /**
     * Update course lifecycle & grades settings via API.
     *
     * Updates course preview buffer, course archive buffer, default minimum passing grade,
     * course grade rounding, and term schedule course finder threshold settings.
     *
     * @param {Object} settingsData - Object containing course lifecycle & grades settings
     *   - course_preview_buffer {number} - Number of days before course start date when preview access is granted
     *   - course_archive_buffer {number} - Number of days after course end date when course is automatically archived
     *   - default_minimum_passing_grade {number} - Default minimum passing grade as a decimal (e.g., 0.75 for 75%)
     *   - course_grade_rounding {boolean} - Enable rounding of course grades to whole numbers
     *   - term_schedule_course_finder_threshold_days {number} - Number of days threshold for term schedule course finder
     * @returns {Promise<Object>} Response data from API
     * @throws {Error} If API request fails or validation errors occur
     * @example
     * const settingsData = {
     *     course_preview_buffer: 3,
     *     course_archive_buffer: 30,
     *     default_minimum_passing_grade: 0.75,
     *     course_grade_rounding: true,
     *     term_schedule_course_finder_threshold_days: 7,
     * };
     *
     * try {
     *     await InstitutionSetting.updateCourseLifecycleGrades(settingsData);
     * } catch (error) {
     *     console.error('Update failed:', error.response?.data?.errors);
     * }
     */
    static async updateCourseLifecycleGrades(settingsData) {
        const response = await InstitutionSetting.api().post(
            '/api/institution-settings/course-lifecycle-grades',
            settingsData
        );

        const responseData = response.response?.data?.data || response.data?.data || response.data || response;

        const updatedSettings = responseData?.settings || responseData;

        if (updatedSettings && typeof updatedSettings === 'object' && !Array.isArray(updatedSettings)) {
            try {
                const settingsArray = Object.keys(updatedSettings).map(key => ({
                    id: key,
                    key,
                    value: updatedSettings[key],
                    type: typeof updatedSettings[key],
                }));

                InstitutionSetting.insertOrUpdate({ data: settingsArray });
            } catch (_vuexOrmError) {
                InstitutionSetting._fallbackCache = { ...InstitutionSetting._fallbackCache, ...updatedSettings };
            }
        }

        return responseData;
    }

    /**
     * Update identity verification settings via API.
     *
     * Updates minimum interval, maximum interval, and lockout period settings for identity verification.
     *
     * @param {Object} settingsData - Object containing identity verification settings
     *   - identity_verification_min_minutes {number} - Minimum time interval in minutes before identity verification expires
     *   - identity_verification_max_minutes {number} - Maximum time interval in minutes before identity verification expires
     *   - identity_verification_minutes_locked_out {number} - Number of minutes a user account is locked after a failed attempt
     * @returns {Promise<Object>} Response data from API
     * @throws {Error} If API request fails or validation errors occur
     * @example
     * const settingsData = {
     *     identity_verification_min_minutes: 10080,
     *     identity_verification_max_minutes: 20160,
     *     identity_verification_minutes_locked_out: 5,
     * };
     *
     * try {
     *     await InstitutionSetting.updateIdentityVerification(settingsData);
     * } catch (error) {
     *     console.error('Update failed:', error.response?.data?.errors);
     * }
     */
    static async updateIdentityVerification(settingsData) {
        const response = await InstitutionSetting.api().post(
            '/api/institution-settings/identity-verification',
            settingsData
        );

        const responseData = response.response?.data?.data || response.data?.data || response.data || response;

        const updatedSettings = responseData?.settings || responseData;

        if (updatedSettings && typeof updatedSettings === 'object' && !Array.isArray(updatedSettings)) {
            try {
                const settingsArray = Object.keys(updatedSettings).map(key => ({
                    id: key,
                    key,
                    value: updatedSettings[key],
                    type: typeof updatedSettings[key],
                }));

                InstitutionSetting.insertOrUpdate({ data: settingsArray });
            } catch (_vuexOrmError) {
                InstitutionSetting._fallbackCache = { ...InstitutionSetting._fallbackCache, ...updatedSettings };
            }
        }

        return responseData;
    }

    /**
     * Update risk & compliance buffers settings via API.
     *
     * Updates risk activity due date buffer delay setting.
     *
     * @param {Object} settingsData - Object containing risk & compliance buffers settings
     *   - risk_activity_due_date_buffer_delay {number} - Number of days activities due dates are considered in the risk calculations for the Early Warning Report
     * @returns {Promise<Object>} Response data from API
     * @throws {Error} If API request fails or validation errors occur
     * @example
     * const settingsData = {
     *     risk_activity_due_date_buffer_delay: 7,
     * };
     *
     * try {
     *     await InstitutionSetting.updateRiskComplianceBuffers(settingsData);
     * } catch (error) {
     *     console.error('Update failed:', error.response?.data?.errors);
     * }
     */
    static async updateRiskComplianceBuffers(settingsData) {
        const response = await InstitutionSetting.api().post(
            '/api/institution-settings/risk-compliance-buffers',
            settingsData
        );

        const responseData = response.response?.data?.data || response.data?.data || response.data || response;

        const updatedSettings = responseData?.settings || responseData;

        if (updatedSettings && typeof updatedSettings === 'object' && !Array.isArray(updatedSettings)) {
            try {
                const settingsArray = Object.keys(updatedSettings).map(key => ({
                    id: key,
                    key,
                    value: updatedSettings[key],
                    type: typeof updatedSettings[key],
                }));

                InstitutionSetting.insertOrUpdate({ data: settingsArray });
            } catch (_vuexOrmError) {
                InstitutionSetting._fallbackCache = { ...InstitutionSetting._fallbackCache, ...updatedSettings };
            }
        }

        return responseData;
    }

    /**
     * Update attestation settings via API.
     *
     * Updates attestation verbiage setting.
     *
     * @param {Object} settingsData - Object containing attestation settings
     *   - attestation_verbiage {string} - Text displayed for academic integrity attestation
     * @returns {Promise<Object>} Response data from API
     * @throws {Error} If API request fails or validation errors occur
     * @example
     * const settingsData = {
     *     attestation_verbiage: 'I attest that I will not share or distribute...',
     * };
     *
     * try {
     *     await InstitutionSetting.updateAttestation(settingsData);
     * } catch (error) {
     *     console.error('Update failed:', error.response?.data?.errors);
     * }
     */
    static async updateAttestation(settingsData) {
        try {
            const response = await InstitutionSetting.api().post('/api/institution-settings/attestation', settingsData);

            const responseData = response.response?.data?.data || response.data?.data || response.data || response;

            const updatedSettings = responseData?.settings || responseData;

            if (updatedSettings && typeof updatedSettings === 'object' && !Array.isArray(updatedSettings)) {
                try {
                    const settingsArray = Object.keys(updatedSettings).map(key => ({
                        id: key,
                        key,
                        value: updatedSettings[key],
                        type: typeof updatedSettings[key],
                    }));

                    InstitutionSetting.insertOrUpdate({ data: settingsArray });
                } catch (vuexOrmError) {
                    console.warn('⚠️ VuexORM update storage failed, updating fallback cache:', vuexOrmError);
                    InstitutionSetting._fallbackCache = { ...InstitutionSetting._fallbackCache, ...updatedSettings };
                }
            }

            return responseData;
        } catch (error) {
            console.error('❌ Update attestation failed:', error);
            throw error;
        }
    }
}
